import React from 'react';
import {toast} from 'react-toastify';
import {DefaultButton, Dialog, DialogFooter, DialogType, MessageBar, MessageBarType, PrimaryButton, TextField} from '@fluentui/react';
import {useApplyFolioQuoteLinkMutation, useGetFolioQuoteLinkPermissionQuery} from '../../store/endpoints/RecordLinksEndpoints';

interface IPsaQuoteLinkDialogProps {
    isOpen: boolean;
    onClose: () => void;
    /** The direct-import (sub-)extension id the quick-flow link targets. */
    extensionId: string;
    /** The owning folio's source id. Enables the "create it for me" path when the connected
     * member may write the provider's quote-link setup table. */
    folioId?: string;
    /** Query string carrying the provider's substitution tag (e.g. "opp=[opportunityrecid]"),
     * appended so the pasted link needs no manual parameter setup. */
    parameter?: string;
    /** One-line explanation from the folio's quoteLink declaration. */
    description?: string;
    /** Install steps from the folio's quoteLink declaration, shown verbatim. */
    instructions?: string[];
}

/**
 * The PSA Quote Link setup dialog: the generated /go/opportunity-import quick-flow URL for an
 * opportunity import extension, plus the provider's install walkthrough. The wording comes from the
 * folio's quoteLink declaration so provider specifics live in folio data, not here. Shared between
 * the import panel and the folio details panel.
 */
const PsaQuoteLinkDialog: React.FC<IPsaQuoteLinkDialogProps> = (props) => {
    const quoteLinkUrl = `${window.location.origin}/go/opportunity-import/${props.extensionId}`
        + (props.parameter ? `?${props.parameter}` : '');
    // Only ask the provider about table permissions while the dialog is actually open.
    const permission = useGetFolioQuoteLinkPermissionQuery(props.folioId ?? '', {
        skip: !props.isOpen || !props.folioId
    });
    const [applyQuoteLink, applyState] = useApplyFolioQuoteLinkMutation();

    const createInPsa = async () => {
        if (!props.folioId) return;
        try {
            const result = await applyQuoteLink({folioId: props.folioId, link: quoteLinkUrl}).unwrap();
            if (result.success === false) {
                toast.error(result.error || 'The quote link could not be created');
                return;
            }
            toast.success(result.alreadyExisted
                ? 'The PSA already has this quote link'
                : 'Quote link created in the PSA');
            props.onClose();
        } catch {
            toast.error('The quote link could not be created');
        }
    };

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={props.onClose}
            minWidth={520}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'PSA Quote Link',
                subText: props.description
                    ?? 'Give your PSA a quote link that opens Adaptive Catalog, imports the opportunity automatically, and lands on a prepared cart ready to deliver.'
            }}>
            <TextField readOnly value={quoteLinkUrl} onFocus={(e) => (e.target as HTMLInputElement).select()} />
            <DefaultButton
                text='Copy Link'
                iconProps={{iconName: 'Copy'}}
                styles={{root: {marginTop: '0.5em'}}}
                onClick={() => {
                    void navigator.clipboard.writeText(quoteLinkUrl);
                    toast.success('Quote link copied');
                }}
            />
            {permission.data?.allowed &&
                <PrimaryButton
                    text='Create It in the PSA for Me'
                    iconProps={{iconName: 'CloudUpload'}}
                    disabled={applyState.isLoading}
                    styles={{root: {marginTop: '0.5em', marginLeft: '1em'}}}
                    onClick={() => { void createInPsa(); }}
                />}
            {permission.data?.allowed === false && permission.data?.success !== false &&
                <MessageBar messageBarType={MessageBarType.info} styles={{root: {marginTop: '0.5em'}}}>
                    The connected API member cannot write the Quote Link setup table, so it has to be
                    added by hand using the steps below.
                </MessageBar>}
            {props.instructions && props.instructions.length > 0 &&
                <ol style={{paddingLeft: '1.25em', lineHeight: '20px'}}>
                    {props.instructions.map((step, i) => <li key={`quote-link-step-${i}`}>{step}</li>)}
                </ol>}
            <DialogFooter>
                <PrimaryButton text='Done' onClick={props.onClose} />
            </DialogFooter>
        </Dialog>
    );
};

export default PsaQuoteLinkDialog;
